import { createStore } from "../lib/store";
import { useStore } from "../lib/useStore";
import { Icon } from "./Icon";

type ToastAction = { label: string; onClick: () => void; testId?: string };
type Toast = { id: number; text: string; kind: "info" | "error" | "ok"; leaving?: boolean; action?: ToastAction };
const toasts = createStore<Toast[]>([]);
let seq = 0;
const LEAVE_MS = 200;

const dismiss = (id: number) => {
  toasts.set((l) => l.map((x) => (x.id === id ? { ...x, leaving: true } : x)));
  setTimeout(() => toasts.set((l) => l.filter((x) => x.id !== id)), LEAVE_MS);
};

/**
 * Transient feedback: one HUD capsule above the dock (a new toast replaces the current one). "ok" and
 * "error" get a leading accent icon. `action`: one thing to do next, in the accent at its end.
 */
export function toast(text: string, kind: Toast["kind"] = "info", ms = 3200, action?: ToastAction) {
  const id = ++seq;
  toasts.set(() => [{ id, text, kind, action }]);
  setTimeout(() => dismiss(id), ms);
}

/** `bottom` overrides the default position above the dock (screens without a dock). */
export function Toasts({ bottom }: { bottom?: string }) {
  const list = useStore(toasts);
  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-[60] flex flex-col items-center gap-2"
      style={{
        bottom: bottom ?? "var(--dock-clearance)",
        left: "var(--nav-w)",
        paddingLeft: "calc(var(--spacing-gutter) + var(--safe-left))",
        paddingRight: "calc(var(--spacing-gutter) + var(--safe-right))",
      }}
      aria-live="polite"
    >
      {list.map((x) => (
        <div key={x.id} className="toast" data-leaving={x.leaving || undefined} role={x.kind === "error" ? "alert" : "status"}>
          {x.kind === "ok" ? <Icon name="check-circle" size={20} weight="fill" className="toast-icon" /> : null}
          {x.kind === "error" ? <Icon name="alert" size={20} weight="fill" className="toast-icon" /> : null}
          <span className="min-w-0">{x.text}</span>
          {x.action ? (
            <button
              type="button"
              className="toast-action"
              onClick={() => {
                x.action!.onClick();
                dismiss(x.id);
              }}
              data-testid={x.action.testId}
            >
              {x.action.label}
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}
